// import { User } from 'firebase/auth';
import { logDebug, logInfo } from "./logger";
import { BooleanLike } from "./SharedUtils";

let fs: any;
let randomUUID: any;
if (process.env.REACT_APP_IS_SERVER === "true") {
	randomUUID = require("crypto").randomUUID;
	fs = require("fs");
}

class AppFeatures {
	private _cacheVersion = 10;
	private _isInitialized: boolean = false;
	public get cacheVersion() {
		return this._cacheVersion;
	}
	private configuration: {
		useXactimateAIDatabaseForMissingMappings?: boolean;
		enableCloudLogging?: boolean;
		classifyImages?: boolean;
		useImageGeneration?: boolean;
		enableCache?: boolean;
		enableCacheForServices?: { [key: string]: boolean };
		productName: string;
		isEstimatics: boolean;
		isBuilder: boolean;
		isDebug: boolean;
		isDevMachine: boolean;
		serverPort?: number;
		deviceID?: string;
		runEmailProcessor?: boolean;
		backupInterval?: number;
		collectionsToBackup?: string[];
		backupDestinationBasePath?: string;
		setMappingsPassword?: string;
		usePrivateDatabaseApp?: boolean;
		overrideServerBaseURL?: string;
		useLocalDiskForStorage?: boolean;
		includedOrganizations?: string[];
		excludedOrganizations?: string[];
		classificationModel?: string;
		lowConfidenceThreshold?: number;
		generateFuzzySearch?: boolean;
		isDevMachineReal?: boolean;
		xaMappingEmails?: string[];
		excludedTPARules?: { [k: string]: any };
	} = {
		enableCache: false,
		isBuilder: false,
		productName: "",
		isEstimatics: true,
		isDebug: false,
		isDevMachine: false,
		serverPort: 0,
		setMappingsPassword: "",
	};

	public init() {
		if (this._isInitialized) {
			return;
		}
		this._isInitialized = true;
		if (process.env.REACT_APP_IS_SERVER === "true") {
			if (fs.existsSync("./serverConfiguration.json")) {
				this.configuration = JSON.parse(fs.readFileSync("./serverConfiguration.json", "utf8"));
				if (this.configuration.deviceID === undefined) {
					this.configuration.deviceID = randomUUID();
					fs.writeFileSync("./serverConfiguration.json", JSON.stringify(this.configuration));
				}
				logInfo("Configuration loaded with deviceID: ", this.configuration.deviceID);
			} else {
				fs.writeFileSync("./serverConfiguration.json", JSON.stringify(this.configuration));
			}
		} else {
			if (process.env.REACT_APP_CONFIGURATION) {
				const config = process.env.REACT_APP_CONFIGURATION;
				const cleanedJson = config.replace(/\\"/g, '"');
				this.configuration = JSON.parse(cleanedJson);
				logDebug("Configuration loaded: ", this.configuration);
			} else {
				logDebug(
					"No configuration found. If you want to create one used this as a template: " +
						JSON.stringify(JSON.stringify(this.configuration))
				);
			}
		}
		this.configuration.useLocalDiskForStorage = this.isDevMachine;
	}

	public shouldShowEngineDebugInfo(user: any): BooleanLike {
		if (user.email === "yakir@and-ventures.com" || user.email === "yakir@46c.group" || user.email === "yakir22@gmail.com") {
			return "Displayed only for Dev users";
		}
		return false;
	}

	public get xaMappingEmails(): string[] {
		return this.configuration.xaMappingEmails ?? [];
	}

	public get useXactimateAIDatabaseForMissingMappings(): boolean {
		return !!this.configuration.useXactimateAIDatabaseForMissingMappings;
	}

	public get generateFuzzySearch(): boolean {
		return !!this.configuration.generateFuzzySearch;
	}

	public get enableCloudLogging(): boolean {
		return !!this.configuration.enableCloudLogging;
	}

	public get lowConfidenceThreshold(): number {
		return this.configuration.lowConfidenceThreshold || 0.7;
	}

	public get classificationModel(): string {
		return this.configuration.classificationModel || "Lasso-First-Form-Classification-Test-Model2";
	}
	public get classifyImages(): boolean {
		return !!this.configuration.classifyImages;
	}

	public get useImageGeneration(): boolean {
		return !!this.configuration.useImageGeneration;
	}

	public get includedOrganizations(): string[] {
		return this.configuration.includedOrganizations || [];
	}
	public get excludedOrganizations(): string[] {
		return this.configuration.excludedOrganizations || [];
	}

	public get overrideServerBaseURL(): string | undefined {
		return this.configuration.overrideServerBaseURL || undefined;
	}

	public get setMappingsPassword(): string {
		return this.configuration.setMappingsPassword || "";
	}

	public get enableCache(): boolean {
		return !!this.configuration.enableCache;
	}
	public getEnableCacheForServices(service: string): boolean {
		return this.configuration.enableCacheForServices?.[service] || false;
	}

	public getTPARuleEnabled(rule: string, subRule?: string): boolean {
		if (this.configuration.excludedTPARules) {
			const ruleData = this.configuration.excludedTPARules[rule];
			if (!subRule) {
				return !ruleData;
			}
			if (ruleData) {
				return !ruleData[subRule];
			}
		}
		return true;
	}

	public get isBuilder(): boolean {
		return !!this.configuration.isBuilder;
	}
	public get productName(): string {
		return this.configuration.productName || "";
	}
	public get deviceID(): string {
		return this.configuration.deviceID || "";
	}

	public get backupInterval(): number {
		return this.configuration.backupInterval || 0;
	}
	public get collectionsToBackup(): string[] {
		return this.configuration.collectionsToBackup ?? [];
	}
	public get backupDestinationBasePath(): string {
		return this.configuration.backupDestinationBasePath || "firestore_backups";
	}

	public get useLocalDiskForStorage(): boolean {
		return !!this.configuration.useLocalDiskForStorage;
	}

	public get isServer(): boolean {
		const typeofLocation = typeof location;
		return process.env.REACT_APP_IS_SERVER === "true" || typeofLocation === "undefined";
	}
	public get isWeb(): boolean {
		return !this.isServer;
	}
	public get serverPort(): number | undefined {
		return this.configuration.serverPort;
	}

	public get usePrivateDatabaseApp(): boolean {
		return this.configuration.usePrivateDatabaseApp || false;
	}

	public get isDevMachineReal(): boolean {
		if (process.env.REACT_APP_IS_SERVER === "true") {
			return !!this.configuration.isDevMachineReal;
		} else {
			return location.href.includes("localhost") || location.href.includes("192.168.") || location.href.includes("127.");
		}
	}
	public get isDevMachine(): boolean {
		if (process.env.REACT_APP_IS_SERVER === "true") {
			return this.configuration.isDevMachine;
		} else {
			return location.href.includes("localhost") || location.href.includes("192.168.") || location.href.includes("127.");
		}
	}

	public get runEmailProcessor(): boolean {
		return !!this.configuration.runEmailProcessor;
	}

	public get isEstimatics(): boolean {
		return !!this.configuration.isEstimatics;
	}

	public isDebug(includeDevServer: boolean = true): boolean {
		let _location = { href: "", host: "" }; // when running on server
		const typeofLocation = typeof location;
		if (typeofLocation === "undefined") {
			// running on server
			return this.configuration.isDebug;
		} else {
			_location = location;
		}
		if (_location.href.includes("production")) {
			return false;
		}
		return (
			(includeDevServer && _location.host.includes("-dev")) ||
			_location.href.includes("localhost") ||
			_location.href.includes("192.168.") ||
			_location.href.includes("127.") ||
			_location.href.includes("view=debug")
		);
	}
}
const appFeatures = new AppFeatures();
export { appFeatures };
